import { ImmutableStore } from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import Immutable from 'immutable';
import FormState from '../constants/FormState';
import SubscriptionStatus from '../constants/SubscriptionStatus';
import Settings from '../utils/Settings';

function defaultState() {
  return {
    subscriptionFormState: FormState.INITIAL,
    sidebarActive: true,
    guideSelections: {
      count: 2,
      term: 'threeYear'
    },
    supportSelections: {
      level: 'standard'
    },
    currentSubscription: {
      status: 'initial',
      paymentPosted: null,
      nextPaymentDue: null,
      term: {
        start: null,
        length: 'threeYear'
      },
      supportLevel: {
        requested: 'basic',
        purchased: 'basic'
      },
      guides: {
        requested: 0,
        purchased: 0
      }
    },
    pricing: {
      guides: {
        oneYear: {
          levels: [
            0,
            0,
            0,
            0,
            0
          ],
          fullDiscount: 0.0
        },
        twoYear: {
          levels: [
            0,
            0,
            0,
            0,
            0
          ],
          fullDiscount: 0.0
        },
        threeYear: {
          levels: [
            0,
            0,
            0,
            0,
            0
          ],
          fullDiscount: 0.0
        }
      },
      support: {
        basic: {
          hours: 0,
          price: 0
        },
        standard: {
          hours: 2,
          price: 0
        },
        advanced: {
          hours: 4,
          price: 0
        }
      }
    },
    currentCost: 0,
    discountedCost: 0
  };
}

class BillingStore extends ImmutableStore {
  static storeName = 'BillingStore';

  static handlers = {
    [Actions.UI__SIDEBAR_TOGGLED]: 'sidebarToggled',
    [Actions.BILLING__GUIDE_COUNT_UPDATED]: 'guideCountChanged',
    [Actions.BILLING__PAYMENT_TERM_UPDATED]: 'paymentTermChanged',
    [Actions.BILLING__SUPPORT_PACKAGE_UPDATED]: 'supportPackageChanged',
    [Actions.BILLING__COST_TIER_LOADED]: 'costTierReceived',
    [Actions.BILLING__SUBMIT_REQUEST_STARTED]: 'billingRequestSubmitted',
    [Actions.BILLING__SUBMIT_REQUEST_COMPLETED]: 'billingRequestCompleted',
    [Actions.BILLING__SUBMIT_REQUEST_FAILED]: 'billingRequestCompleted',
    [Actions.BILLING__REQUEST_STARTED]: 'BILLING__REQUEST_SUBMIT_STARTED',
    [Actions.BILLING__REQUEST_CANCELLED]: 'billingRequestCompleted',
    [Actions.BILLING__SUBSCRIPTION_LOADED]: 'subscriptionReceived',
    [Actions.BILLING__TRANSACTION_HISTORY_LOADED]: 'transactionHistoryReceived'
  };

  constructor(dispatcher) {
    super(dispatcher);
    this.setState(defaultState());
    this.calculateCost();
  }

  costTierReceived(pricing) {
    this.mergeState({
      pricing
    });
    this.calculateCost();
  }

  subscriptionReceived(subscription) {
    const currentSubscription = Immutable.fromJS(subscription);

    this.mergeState({
      currentSubscription,
      guideSelections: {
        count: currentSubscription.getIn(['guides', 'requested']) || 2,
        term: currentSubscription.getIn(['term', 'length']) || 'threeYear'
      },
      supportSelections: {
        level: currentSubscription.getIn(['supportLevel', 'requested']) || 'standard'
      },
    });
  }

  sidebarToggled() {
    this.mergeState({
      sidebarActive: !this.isSidebarActive()
    });
  }

  isSidebarActive() {
    return this.get('sidebarActive');
  }

  guideCountChanged(up) {
    const guideSelection = this.get('guideSelections').toJS();
    const guideCount = guideSelection.count || 2;
    const newCount = up ? guideCount + 1 : guideCount - 1;
    if (newCount > 0 && newCount < 6) {
      this.mergeState({
        guideSelections: {
          ...guideSelection,
          count: newCount
        }
      });
    }
    this.calculateCost();
  }

  paymentTermChanged(term) {
    const guideSelection = this.get('guideSelections').toJS();

    this.mergeState({
      guideSelections: {
        ...guideSelection,
        term
      }
    });
    this.calculateCost();
  }

  supportPackageChanged(level) {
    const supportSelection = this.get('supportSelections').toJS();

    this.mergeState({
      supportSelections: {
        ...supportSelection,
        level
      }
    });
    this.calculateCost();
  }

  getCurrentSubscription() {
    const subscription = this.get('currentSubscription');
    return subscription ? subscription.toJS() : null;
  }

  isCurrentSubscriptionActive() {
    if (Settings.envFeatures.get('bigBank')) {
      return true;
    }
    return this.getCurrentSubscription().status === SubscriptionStatus.ACTIVE;
  }

  getPurchasedGuides() {
    return this.get(['currentSubscription', 'guides', 'purchased']) || 0;
  }

  getSelections() {
    return {
      guides: this.get('guideSelections').toJS(),
      support: this.get('supportSelections').toJS()
    };
  }

  calculateCost() {
    const pricing = this.get('pricing');
    if (pricing) {
      const guideSelections = this.get('guideSelections');
      const supportLevel = this.get(['supportSelections', 'level']) || 'basic';
      const supportCost = pricing.getIn(['support', supportLevel, 'price']) || 0;

      let guideCount = guideSelections.get('count') || 2;
      if (guideCount > 5) {
        guideCount = 5;
      }

      const guideTerm = guideSelections.get('term') || 'threeYear';
      const guideCost = pricing.getIn(['guides', guideTerm, 'levels']).toJS()[guideCount - 1];
      const fullDiscount = 1 - pricing.getIn(['guides', guideTerm, 'fullDiscount']);
      const discountedGuideCost = guideCost * fullDiscount;
      const discountedSupportCost = supportCost * fullDiscount;

      this.mergeState({
        currentCost: guideCost + supportCost,
        discountedCost: discountedGuideCost + discountedSupportCost
      });
    }
  }

  getPricing() {
    return this.get('pricing') ? this.get('pricing').toJS() : null;
  }

  getCurrentCost() {
    return this.get('currentCost') || 0;
  }

  getDiscountedCost() {
    return this.get('discountedCost') || 0;
  }

  billingRequestSubmitted() {
    this.mergeState({
      subscriptionFormState: FormState.PROCESSING
    });
  }

  billingRequestCompleted(error) {
    if (error) {
      this.mergeState({
        subscriptionFormState: FormState.ERROR
      });
    } else {
      this.mergeState({
        subscriptionFormState: FormState.COMPLETED
      });
    }
  }

  BILLING__REQUEST_SUBMIT_STARTED() {
    this.mergeState({
      subscriptionFormState: FormState.UNSAVED
    });
  }

  getSubscriptionFormState() {
    return this.get('subscriptionFormState');
  }

  getTransactionHistory() {
    const transactions = this.get('transactionHistory');

    return transactions ? transactions.toJS() : [];
  }

  transactionHistoryReceived(transactionHistory) {
    transactionHistory.forEach(transaction => {
      transaction.timestamp = new Date(transaction.timestamp);
    });
    this.mergeState({
      transactionHistory
    });
  }

  rehydrate(state) {
    if (state.transactionHistory) {
      state.transactionHistory.forEach(transaction => {
        transaction.timestamp = new Date(transaction.timestamp);
      });
    }
    super.rehydrate(state);
  }
}

export default BillingStore;



// WEBPACK FOOTER //
// ./src/stores/BillingStore.js